/**
 * Linha de log com nível colorido — usada por todos os terminais do produto.
 *
 * Existia duplicada em `ServiceLogTerminal` e `AssistantLogs`, com o mesmo
 * código repetido; corrigir a cor em um deixava o outro para trás. Aqui é o
 * lugar único.
 *
 * Duas regras que mantêm o terminal legível em leitura longa:
 *
 * 1. A cor vive no SELO, não na mensagem. Colorir a linha inteira transforma a
 *    tela num bloco monocromático saturado e cansa. A mensagem só ganha cor
 *    quando exige atenção — erro e aviso.
 * 2. Os tons vêm dos tokens `-fg` do contrato (PBI 84883), que são as variantes
 *    claras e dessaturadas dos semânticos, feitas para ler sobre superfície
 *    escura. Nada de cor fixa: resolve nos dois temas.
 */

interface LevelStyle {
  badge: string;
  message: string;
}

const LEVEL_STYLES: Record<string, LevelStyle> = {
  ERROR: { badge: "text-error-fg bg-error-bg", message: "text-error-fg" },
  WARN: { badge: "text-warning-fg bg-warning-bg", message: "text-warning-fg" },
  INFO: { badge: "text-info-fg bg-info-bg", message: "text-foreground" },
  DEBUG: { badge: "text-faint bg-muted", message: "text-muted-foreground" },
};

/** Abreviações usadas pelos serviços (Serilog e afins). */
const LEVEL_ALIASES: Record<string, string> = {
  ERR: "ERROR",
  WRN: "WARN",
  INF: "INFO",
  DBG: "DEBUG",
};

/** Resolve o estilo de um nível, tolerando abreviação e valor desconhecido. */
const resolveLogLevelStyle = (level: string): LevelStyle => {
  const raw = level.toUpperCase();
  return LEVEL_STYLES[LEVEL_ALIASES[raw] ?? raw] ?? LEVEL_STYLES.DEBUG;
};

export interface LogLineProps {
  timestamp: string;
  level: string;
  message: string;
}

export const LogLine = ({ timestamp, level, message }: LogLineProps) => {
  const style = resolveLogLevelStyle(level);

  return (
    <div className={style.message}>
      <span className="text-faint text-xs mr-2">[{timestamp}]</span>
      <span
        className={`${style.badge} font-mono text-[10px] font-medium px-1.5 py-0.5 rounded mr-2 inline-block min-w-[3rem] text-center`}
      >
        {level}
      </span>
      {message}
    </div>
  );
};
